import { DATETIME_FORMAT, TIME_FORMAT } from '@/constants';
import { Datetime, getClasses } from '@/utils';
import { ForwardRefExoticComponent, forwardRef, useCallback } from 'react';
import { IncidentIssueWithCacheCrewInvolvedEnum } from '@/models/gen/graphql';

import SimpleTableCell from '@/components/SimpleTable/SimpleTableCell';
import SimpleTableRow from '@/components/SimpleTable/SimpleTableRow';
import { useIncidentHistory } from '@/features/IncidentHistory';
import { useIncidentHistoryDrawer } from '@/features/IncidentHistory/components/IncidentHistoryDrawer';

const IncidentHistoryTableRow = ({ id }: { id: string }, ref: ForwardRefExoticComponent<HTMLDivElement>): JSX.Element => {
  const [{ incident, selected }, setState] = useIncidentHistory(({ incidents, selected }) => ({
    incident: incidents.get(id),
    selected: selected.has(id),
  }));
  const setIncidentHistoryDrawer = useIncidentHistoryDrawer(({ setState }) => setState);

  const onSelect = useCallback(
    (): void =>
      setState((current) => {
        const newSelected = new Map(current.selected);
        if (newSelected.has(id)) newSelected.delete(id);
        else newSelected.set(id, current.incidents.get(id));
        return { ...current, selected: newSelected };
      }),
    [incident, id, setState]
  );
  const onEdit = (): void => {
    setIncidentHistoryDrawer((current) => ({
      ...current,
      show: true,
      data: incident,
    }));
  };

  return (
    <SimpleTableRow id={id} className={getClasses(selected ? 'SELECTED' : undefined)} onDoubleClick={onEdit} ref={ref}>
      <SimpleTableCell.Select checked={selected} onChange={onSelect} />
      <SimpleTableCell name="STATION">{incident.station}</SimpleTableCell>
      <SimpleTableCell name="INCIDENT_DATETIME">{new Datetime(incident.incidentDatetime).format(DATETIME_FORMAT)}</SimpleTableCell>
      <SimpleTableCell name="CREATED_AT">{new Datetime().setAsUTC(incident.createdAt).toLocaleDatetime().frontendDatetime}</SimpleTableCell>
      <SimpleTableCell name="TRIP_SERVICER_IATA_AIRLINE_CODE">{incident.tripServicerIataAirlineCode}</SimpleTableCell>
      <SimpleTableCell name="TRIP_FLIGHT_NAME">
        {incident.tripLoopName || (incident.tripFlightNumber ? `${incident.tripFlightNumber || 0}`.padStart(4, '0') : null)}
      </SimpleTableCell>
      <SimpleTableCell name="TRIP_SCHEDULED">
        {incident.tripId ? new Datetime(incident.tripScheduled).format(TIME_FORMAT) : null}
      </SimpleTableCell>
      <SimpleTableCell name="VEHICLE_TRACKING_ID">{incident.vehicleTrackingId}</SimpleTableCell>
      <SimpleTableCell name="EMPLOYEE_NAME">{[incident.driverFirstName, incident.driverLastName].join(' ')}</SimpleTableCell>
      <SimpleTableCell name="DRIVER_EMPLOYEE_ID">{incident.driverEmployeeId}</SimpleTableCell>
      <SimpleTableCell name="MANAGER_NAME">
        {incident.managerId ? [incident.managerFirstName, incident.managerLastName].join(' ') : null}
      </SimpleTableCell>
      <SimpleTableCell
        name="CREW_INVOLVED"
        style={{ color: incident.crewInvolved === IncidentIssueWithCacheCrewInvolvedEnum.Yes ? 'var(--bs-danger)' : 'var(--bs-green)' }}
      >
        {incident.crewInvolved}
      </SimpleTableCell>
    </SimpleTableRow>
  );
};

export default forwardRef(IncidentHistoryTableRow);
